import { logGlobalError } from '../utils/globalErrorLogger';
import { reloadForLostDatabaseConnection } from './appUpdate';
import { logEventAsync } from './logger';

let installed = false;

/**
 * Browser noise that reaches `window.onerror` with no defect behind it.
 *
 * A ResizeObserver whose callback queue does not drain within one animation
 * frame reports this, by design, in every engine. It means the browser deferred
 * the remaining callbacks to the next frame — not that work was dropped or that
 * the UI is wrong. It arrives with no error object, so the stack is synthesized
 * here and the third-party check below cannot see it for what it is.
 */
const BENIGN_BROWSER_MESSAGES = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
];

function isBenignBrowserMessage(message: string): boolean {
  return BENIGN_BROWSER_MESSAGES.some((benign) => message.includes(benign));
}

/**
 * Wording produced only by a browser or extension writing directly onto the
 * global scope of the page it is hosting — not by anything this app defines.
 * `window.ethereum` is injected by wallet extensions (MetaMask and its many
 * clones); `window.__firefox__` is Firefox for iOS's native-bridge global,
 * present on that browser regardless of the site. Reading the Open Graph
 * `og:type` meta tag is the same story on iOS Safari: some injected
 * share/reader script assumes every page has one, and `index.html` has none,
 * so `querySelector` returns null. When that injected code
 * throws, it does so as if it were first-party: `event.error` is a real
 * `Error`, and its single frame is stamped "global code" at the page's own
 * URL rather than an extension scheme, so neither `isBenignBrowserMessage`
 * nor the third-party stack checks below can recognize it. The message text
 * naming a global this app never touches is the only reliable signal.
 */
const INJECTED_GLOBAL_MESSAGES = [
  "evaluating 'window.ethereum",
  "evaluating 'window.__firefox__",
  "Can't find variable: __firefox__",
  "meta[property='og:type']",
];

function isInjectedGlobalNoise(message: string): boolean {
  return INJECTED_GLOBAL_MESSAGES.some((known) => message.includes(known));
}

/**
 * Wording that only a browser extension's own messaging layer produces. An
 * installed extension's content script talks to its service worker on every
 * page it runs on, including ours; when the other end goes away mid-exchange —
 * tab navigation, service-worker eviction, extension reload — the send promise
 * rejects into this page's `unhandledrejection` handler.
 *
 * Matched on text because the stack is useless here: these reasons arrive
 * either with no frames at all or with only the shared relay frame from our own
 * bundle, so `hasOnlyThirdPartyFrames` cannot recognize them. None of these
 * strings can come from application code — nothing here speaks
 * `chrome.runtime`/`browser.runtime`, and the `tabs:<event>` names in
 * "No Listener" are an extension bus's, not ours.
 */
const EXTENSION_MESSAGING_MESSAGES = [
  'A listener indicated an asynchronous response by returning true, but the message channel closed',
  'The message port closed before a response was received',
  'Could not establish connection. Receiving end does not exist',
  'Invalid call to runtime.sendMessage()',
  'Extension context invalidated',
  'No Listener:',
];

function isExtensionMessagingError(reason: unknown): boolean {
  const message =
    reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : '';
  return EXTENSION_MESSAGING_MESSAGES.some((known) => message.includes(known));
}

/**
 * EIP-1193's 4900 wallet-provider disconnect message. This app has no Ethereum
 * integration; injected wallets can reject with a string or a serialized RPC
 * error, leaving only our `toError` / rejection-handler frames in the report.
 * Match the complete message so ordinary Firebase or payment connection
 * failures remain reportable. Revisit this if we add a wallet integration.
 */
function isWalletProviderDisconnect(reason: unknown): boolean {
  const message =
    reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : '';
  return message === 'The provider is disconnected from all chains.';
}

/**
 * The Firebase SDK's wording when the browser closes the IndexedDB connection
 * it relies on. Matched on text because it surfaces as a bare `UnknownError`
 * with an empty stack — there is no code or type to key on.
 */
function isLostDatabaseConnection(reason: unknown): boolean {
  return reason instanceof Error
    && reason.message.includes('Connection to Indexed Database server lost');
}

/**
 * Firebase's IndexedDB Auth persistence wording when the browser tears down
 * the connection during a hidden/pagehide transition. This can still arrive
 * from an already-loaded tab or from another Firebase consumer, even though
 * the app now uses local-storage Auth persistence. The in-memory session is
 * still valid, so this is a lifecycle event rather than an application fault.
 */
export function isDatabaseClosingOrHiddenError(reason: unknown): boolean {
  return reason instanceof Error && reason.message.includes('Database is closing/hidden');
}

interface UnhandledRejectionEventLike {
  reason: unknown;
  preventDefault: () => void;
}

function handleUnhandledRejection(event: UnhandledRejectionEventLike): void {
  const reason = event.reason;
  if (reason == null) return;
  const error = toError(reason);
  if (!isReportableRejection(error)) return;

  // Firebase may still have an IndexedDB persistence operation in flight in
  // a tab that was loaded before this mitigation, or in a browser transition
  // that overlaps the SDK's own cleanup. Do not let an expected lifecycle race
  // become an uncaught-console error or a Cloud Error Reporting occurrence.
  if (isDatabaseClosingOrHiddenError(error)) {
    event.preventDefault();
    void logEventAsync('INFO', 'firebase_auth_persistence_lifecycle_race', {
      source: 'firebaseAuthBackgroundRefresh',
    });
    return;
  }

  // A lost IndexedDB connection leaves auth state stuck, so the tab is
  // already unusable. Recover it once rather than reporting an error the user
  // can only fix by reloading anyway; WARNING keeps the occurrence visible
  // without filing it as a defect.
  if (isLostDatabaseConnection(error)) {
    const reloading = reloadForLostDatabaseConnection();
    logGlobalError(error, {
      context: 'unhandledrejection',
      severity: 'WARNING',
      metadata: { recovery: reloading ? 'reloading' : 'alreadyReloadedThisSession' },
    });
    return;
  }

  logGlobalError(error, { context: 'unhandledrejection' });
}

/**
 * Frame locations that can never be first-party code: browser extension
 * schemes (`chrome-extension://`, `moz-extension://`, `safari-web-extension://`)
 * and WebKit's `webkit-masked-url://`, which is how Safari deliberately hides
 * injected content-script frames. `[native code]` is the engine itself.
 *
 * reCAPTCHA is listed by its exact CDN path rather than by host: the app only
 * ever loads Google's reCAPTCHA script from there, and its deferred callbacks
 * touch DOM nodes it manages itself, long after our container is gone. Other
 * `gstatic.com` paths are deliberately not covered — the Firebase SDK is
 * bundled from npm here, but a CDN copy would be somebody's real dependency.
 */
const THIRD_PARTY_FRAME =
  /(?:extension|masked-url):\/\/|\[native code\]|https:\/\/www\.gstatic\.com\/recaptcha\//;

/** A stack line that names a call site, in either V8 or SpiderMonkey/JSC form. */
function isStackFrame(line: string): boolean {
  return /^at\s/.test(line) || /^\S*@\S/.test(line);
}

/** The stack's call-site lines, innermost first, without the V8 header line. */
function stackFrames(error: Error): string[] {
  const stack = error.stack;
  if (!stack) return [];

  // V8 prefixes the stack with "Name: message"; JSC and SpiderMonkey do not.
  const header = `${error.name}: ${error.message}`;
  const body = stack.startsWith(header) ? stack.slice(header.length) : stack;

  return body
    .split('\n')
    .map((line) => line.trim())
    .filter(isStackFrame);
}

/**
 * True when the error's stack is made up entirely of extension, masked, or
 * native frames — i.e. an injected script rejected into our handler and no
 * first-party code took part.
 *
 * Deliberately conservative: an unparseable stack, a missing stack, or a single
 * recognizable application frame all mean "report it". The point is to drop
 * only what provably has nothing to do with this app, not to guess.
 */
function hasOnlyThirdPartyFrames(error: Error): boolean {
  const frames = stackFrames(error);
  if (frames.length === 0) return false;
  return frames.every((frame) => THIRD_PARTY_FRAME.test(frame));
}

/**
 * Frame locations only an injected script can occupy: extension schemes and
 * WebKit's masked alias for them. A strict subset of `THIRD_PARTY_FRAME` —
 * `[native code]` is the engine constructing errors on anyone's behalf, and
 * the reCAPTCHA path is a first-party dependency, so neither proves an
 * extension made the error.
 */
const INJECTED_SCRIPT_FRAME = /(?:extension|masked-url):\/\//;

/**
 * True when the error object was constructed inside an injected extension
 * script: the innermost frame — the construction site — is an extension frame,
 * even though application frames appear beneath it.
 *
 * This is the signature of an extension's `window.fetch` wrapper. Chromium
 * stamps a fetch `TypeError` with the synchronous stack of the `fetch()` call,
 * so the wrapper sits on top and the app's caller frames underneath — which is
 * exactly why `hasOnlyThirdPartyFrames` keeps it. But the app's caller does not
 * own the rejection that reached the global handler: wrappers chain their own
 * `.then()` housekeeping onto the real response promise, and it is that
 * orphaned chain that rejects unhandled on a network failure the app's own
 * `catch` already absorbed (in production: the once-a-minute `/version.json`
 * poll, whose failures `checkForUpdate` swallows by design).
 *
 * Dropping these is safe for the same reason as `hasOnlyThirdPartyFrames`: if
 * application code genuinely fails to handle a rejection, the same failure
 * also fires for the extension-free majority, and those reports carry a
 * first-party construction frame and are kept.
 */
function wasConstructedInInjectedScript(error: Error): boolean {
  const frames = stackFrames(error);
  return frames.length > 0 && INJECTED_SCRIPT_FRAME.test(frames[0]);
}

/**
 * Rejections that are not application faults and must not be reported.
 *
 * `AbortError` means something cancelled a request on purpose — a component
 * unmounted, the user navigated, or the page is tearing down with fetches in
 * flight. WebKit words it "The user aborted a request."; Chrome says "signal is
 * aborted without reason". Neither is a defect, and page-teardown aborts alone
 * accounted for the single noisiest signal in production.
 *
 * A rejection with no reason at all (`Promise.reject()`, or an injected
 * extension script rejecting into our handler) can only ever be reported as
 * "Error: undefined" — there is no message, no stack, and nothing to act on.
 *
 * A rejection whose stack contains no first-party frame is somebody else's
 * script failing inside our page — browser extensions monkey-patch `fetch` and
 * reject into the global handler, and Safari masks their frames so the message
 * often degrades to a bare "Error: undefined". There is no application fix for
 * these and they crowd out real signal.
 *
 * A rejection *constructed* inside an extension is that extension's own promise
 * chain failing even when our frames appear further down the stack — most often
 * a `window.fetch` wrapper whose housekeeping chain rejects alongside a request
 * the app already handles; see `wasConstructedInInjectedScript`.
 *
 * An extension's messaging layer failing mid-exchange is the same thing without
 * a usable stack to prove it, so it is recognized by wording instead; see
 * `EXTENSION_MESSAGING_MESSAGES`.
 */
function isReportableRejection(reason: unknown): boolean {
  if (reason == null) return false;
  if (reason instanceof DOMException && reason.name === 'AbortError') return false;
  if (reason instanceof Error && reason.name === 'AbortError') return false;
  if (isExtensionMessagingError(reason)) return false;
  if (isWalletProviderDisconnect(reason)) return false;
  if (reason instanceof Error && hasOnlyThirdPartyFrames(reason)) return false;
  if (reason instanceof Error && wasConstructedInInjectedScript(reason)) return false;
  return true;
}

/**
 * Best-effort description of a rejection reason that isn't an `Error`, so the
 * report says what was thrown instead of collapsing to "[object Object]".
 */
function describeReason(reason: unknown): string {
  if (typeof reason === 'string') return reason;
  if (typeof reason === 'object') {
    const message = (reason as { message?: unknown }).message;
    if (typeof message === 'string' && message) return message;
    try {
      return `Non-Error rejection: ${JSON.stringify(reason)}`;
    } catch {
      return `Non-Error rejection: ${Object.prototype.toString.call(reason)}`;
    }
  }
  return `Non-Error rejection: ${String(reason)}`;
}

/**
 * The rejection reason as an `Error`, keeping its own name and stack when it
 * carries them.
 *
 * An error thrown in another realm — an iframe, or an extension's isolated
 * world — is a real `TypeError` or `Error` there but fails `instanceof Error`
 * here. Wrapping it in a fresh `Error` would discard its stack for one
 * constructed inside this handler, so every such report would point at the
 * same bundle frame (ours) instead of the real throw site, and the
 * third-party frame checks in `isReportableRejection` would never see the
 * extension frames that could clear it.
 */
function toError(reason: unknown): Error {
  if (reason instanceof Error) return reason;
  const error = new Error(describeReason(reason));
  if (typeof reason === 'object' && reason !== null) {
    const { name, stack } = reason as { name?: unknown; stack?: unknown };
    if (typeof name === 'string' && name) error.name = name;
    if (typeof stack === 'string' && stack) error.stack = stack;
  }
  return error;
}

/**
 * Install one-shot global listeners that forward uncaught errors and unhandled
 * promise rejections to Cloud Logging via `logGlobalError`.
 *
 * Caveats:
 * - The underlying log call requires auth; errors thrown before sign-in are
 *   dropped rather than buffered.
 * - React render-phase errors do NOT bubble to window.onerror — the app-level
 *   ErrorBoundary covers that path.
 * - Deliberate cancellations, reason-less rejections, and failures raised
 *   entirely inside injected third-party scripts are filtered out; see
 *   `isReportableRejection`.
 */
export function installErrorReporter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    if (event.message === 'Script error.' || event.message === 'Script error') return;
    if (isBenignBrowserMessage(event.message)) return;
    if (isInjectedGlobalNoise(event.message)) return;
    const error = event.error instanceof Error ? event.error : new Error(event.message);
    if (!isReportableRejection(error)) return;
    logGlobalError(error, {
      context: 'window.onerror',
      metadata: {
        source: event.filename,
        line: event.lineno,
        column: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', handleUnhandledRejection);
}

export const _internal = {
  isReportableRejection,
  describeReason,
  toError,
  hasOnlyThirdPartyFrames,
  wasConstructedInInjectedScript,
  isBenignBrowserMessage,
  isInjectedGlobalNoise,
  isExtensionMessagingError,
  isLostDatabaseConnection,
  isDatabaseClosingOrHiddenError,
  handleUnhandledRejection,
};
